import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useRunStore } from "../state/runStore";
import { slugifySeriesName } from "../utils/slug";

function formatDateRange(start: string | null, end: string | null) {
  if (!start) return "";
  try {
    const startDate = new Date(start);
    const startLabel = startDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    if (!end) return startLabel;
    const endDate = new Date(end);
    const endLabel = endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${startLabel} - ${endLabel}`;
  } catch {
    return "";
  }
}

export default function TrackWeekSelector() {
  const { teamId, seriesSlug, trackWeekId } = useParams<{
    teamId: string;
    seriesSlug: string;
    trackWeekId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const activeRef = useRef<HTMLButtonElement | null>(null);
  const { trackWeeks, selectedTrackWeekId, setTrackWeek } = useRunStore();

  const scopedWeeks = useMemo(() => {
    const filtered = seriesSlug
      ? trackWeeks.filter((tw) => slugifySeriesName(tw.seriesName) === seriesSlug)
      : trackWeeks;

    return [...filtered].sort((a, b) => {
      const aStarts = a.startsAt || "";
      const bStarts = b.startsAt || "";
      return bStarts.localeCompare(aStarts);
    });
  }, [seriesSlug, trackWeeks]);

  const selectedId = trackWeekId || selectedTrackWeekId;
  const currentTool =
    location.pathname.match(/\/weeks\/[^/]+\/([^/]+)/)?.[1] || "overview";
  const currentWeek = scopedWeeks.find((tw) => tw.id === selectedId) || null;

  useEffect(() => {
    if (!activeRef.current) return;
    activeRef.current.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedId, scopedWeeks.length]);

  if (scopedWeeks.length === 0) {
    return (
      <div style={styles.empty}>
        No weeks configured for this {seriesSlug ? "series" : "team"} yet.
      </div>
    );
  }

  const label = seriesSlug ? "Season Timeline" : "Track Weeks";
  const helper = seriesSlug
    ? "Jump between weeks in this series"
    : "Jump between configured track weeks";

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <div>
          <div style={styles.eyebrow}>{label}</div>
          <div style={styles.helper}>{helper}</div>
        </div>
        {currentWeek && (
          <div style={styles.currentMeta}>
            <span style={styles.currentBadge}>
              W{currentWeek.displayWeekNumber ?? currentWeek.weekNumber ?? "-"}
            </span>
            {formatDateRange(currentWeek.startsAt, currentWeek.endsAt) && (
              <span style={styles.currentDates}>
                {formatDateRange(currentWeek.startsAt, currentWeek.endsAt)}
              </span>
            )}
          </div>
        )}
      </div>

      <div style={styles.container}>
        <div style={styles.fadeLeft} />
        <div style={styles.fadeRight} />
        <div style={styles.scroll}>
          {scopedWeeks.map((tw) => {
            const isActive = tw.id === selectedId;
            return (
              <button
                key={tw.id}
                ref={isActive ? activeRef : null}
                onClick={() => {
                  setTrackWeek(tw.id);
                  if (teamId && seriesSlug) {
                    navigate(
                      `/teams/${teamId}/series/${seriesSlug}/weeks/${tw.id}/${currentTool}`
                    );
                  }
                }}
                style={{
                  ...styles.tab,
                  ...(isActive ? styles.tabActive : null),
                }}
              >
                <div style={styles.tabTop}>
                  <span style={styles.weekPill}>
                    W{tw.displayWeekNumber ?? tw.weekNumber ?? "-"}
                  </span>
                  {tw.runCount != null && (
                    <span style={styles.runsPill}>
                      {tw.runCount} {tw.runCount === 1 ? "run" : "runs"}
                    </span>
                  )}
                </div>
                <div style={styles.trackName}>{tw.trackName}</div>
                {formatDateRange(tw.startsAt, tw.endsAt) && (
                  <div style={styles.dateLabel}>
                    {formatDateRange(tw.startsAt, tw.endsAt)}
                  </div>
                )}
                {isActive && <span style={styles.activeBar} />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "0.9rem",
  },
  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "1rem",
    flexWrap: "wrap",
  },
  eyebrow: {
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
  },
  helper: {
    fontFamily: "var(--font-body)",
    fontSize: "0.94rem",
    color: "var(--text-secondary)",
    marginTop: "0.22rem",
    fontWeight: 600,
  },
  currentMeta: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    flexWrap: "wrap",
  },
  currentBadge: {
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "var(--text-secondary)",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid var(--rule)",
    borderRadius: 999,
    padding: "0.24rem 0.62rem",
  },
  currentDates: {
    fontFamily: "var(--font-body)",
    fontSize: "0.84rem",
    fontWeight: 600,
    color: "var(--text-secondary)",
  },
  container: {
    position: "relative",
    background: "var(--ink-1)",
    border: "1px solid var(--rule)",
    borderRadius: "var(--r-lg)",
    overflow: "hidden",
  },
  scroll: {
    display: "flex",
    gap: "0.75rem",
    overflowX: "auto",
    padding: "0.9rem 1rem",
    scrollBehavior: "smooth",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  fadeLeft: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    width: 20,
    background: "linear-gradient(90deg, var(--ink-2) 10%, transparent)",
    pointerEvents: "none",
    zIndex: 1,
  },
  fadeRight: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: 20,
    background: "linear-gradient(270deg, var(--ink-2) 10%, transparent)",
    pointerEvents: "none",
    zIndex: 1,
  },
  tab: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minWidth: 250,
    gap: "0.55rem",
    padding: "1.1rem 1.1rem 1.05rem",
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: "var(--r-md)",
    color: "var(--text-strong)",
    cursor: "pointer",
    textAlign: "left",
    flexShrink: 0,
    transition: "transform 0.2s ease, border-color 0.2s ease, background 0.2s ease",
  },
  tabActive: {
    background: "var(--live-soft)",
    border: "1px solid var(--live-edge)",
    transform: "translateY(-1px)",
  },
  tabTop: {
    display: "flex",
    alignItems: "center",
    gap: "0.45rem",
    flexWrap: "wrap",
  },
  weekPill: {
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "var(--live)",
    background: "var(--live-soft)",
    border: "1px solid var(--live-edge)",
    borderRadius: 999,
    padding: "0.18rem 0.5rem",
  },
  runsPill: {
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "var(--text-secondary)",
    background: "rgba(255,255,255,0.04)",
    borderRadius: 999,
    padding: "0.15rem 0.45rem",
  },
  trackName: {
    fontFamily: "var(--font-display)",
    fontSize: "1.08rem",
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: "-0.03em",
    color: "var(--text-strong)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical" as const,
  },
  dateLabel: {
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "var(--text-secondary)",
  },
  activeBar: {
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 0,
    height: 2,
    borderRadius: 999,
    background: "var(--live)",
  },
  empty: {
    fontFamily: "var(--font-body)",
    fontSize: "0.96rem",
    color: "var(--text-secondary)",
    padding: "0.75rem 0",
  },
};
